import { useMutation } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowRight,
  Barcode,
  CheckCircle2,
  FileCode2,
  Link2,
  PackagePlus,
  Sparkles,
  Upload,
  UserPlus,
} from 'lucide-react'
import { useMemo, useRef, useState, type ReactNode } from 'react'

import type {
  NfeAnalysis,
  NfeItemDecision,
  NfePlanItem,
} from '@aeris/contracts'
import { formatMoney } from '@aeris/money'
import { parseAmount, ZERO_AMOUNT } from '@aeris/purchasing'
import {
  Badge,
  Button,
  EmptyState,
  Page,
  PageHeader,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { analyzeNfe, ApiError, importNfe } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { useWorkspace } from '../lib/workspace.ts'

/**
 * Importing a supplier's NF-e.
 *
 * Three steps, and the middle one is the whole screen: choose the file, decide
 * what each line IS, import. The decision step exists because matching an
 * invoice to a catalogue is guesswork with money attached — a line linked to
 * the wrong product writes a cost onto it, and nothing afterwards looks broken.
 *
 * What the screen refuses to do:
 *
 *   - It does not pre-tick anything it is unsure about. A `review` line starts
 *     with no decision, and a line with no decision is not imported. F018
 *     settled that: defaulting an unanswered question to "yes" makes the review
 *     theatre.
 *   - It does not offer to create the supplier. When the emitter is not
 *     registered the import stops and points at Pessoas — the product owner's
 *     decision of 2026-08-10, because a supplier is a relationship somebody
 *     chose to have, not a side effect of opening a file.
 */

/** A partir daqui, "parecido": o que "Aceitar as sugestões" aceita (F248). */
const LIKELY = 0.7

/** "1 produto criado", "3 produtos criados" — e não "produto(s) criado(s)". */
const counted = (count: number, one: string, many: string): string =>
  `${String(count)} ${count === 1 ? one : many}`

/** "1 item", "3 itens" — e não "item(ns)". */
const items = (count: number): string => counted(count, 'item', 'itens')

/** How confident the machine is, in words a person can weigh. */
const confidenceLabel = (value: number | null): string =>
  value === null
    ? ''
    : value >= 0.85
      ? 'muito parecido'
      : value >= LIKELY
        ? 'parecido'
        : 'pouco parecido'

const BASIS_LABELS: Record<NfePlanItem['basis'], string> = {
  gtin: 'Código de barras',
  'supplier-code': 'Código do fornecedor',
  description: 'Descrição parecida',
  none: 'Sem correspondente',
}

/**
 * What each refusal means, and what to do about it.
 *
 * Every one of them names an action, because a refusal that only says "no"
 * makes the person guess what would make it a yes.
 */
const REFUSALS: Record<
  NonNullable<NfeAnalysis['refusal']>,
  {
    title: string
    body: string
    action: 'register-supplier' | 'add-role' | null
  }
> = {
  'supplier-not-registered': {
    title: 'Este fornecedor ainda não está cadastrado',
    body: 'Cadastre-o em Pessoas com o papel de fornecedor e importe de novo. Os dados abaixo vieram da própria nota.',
    action: 'register-supplier',
  },
  'supplier-not-a-supplier': {
    title: 'Este cadastro existe, mas não é um fornecedor',
    body: 'Ele está cadastrado sem o papel de fornecedor. Adicione o papel em Pessoas e importe de novo.',
    action: 'add-role',
  },
  'nfce-is-a-sale': {
    title: 'Esta é uma NFC-e, que é uma venda no varejo',
    body: 'Importá-la como compra acrescentaria estoque de mercadoria que acabou de sair da loja.',
    action: null,
  },
  'issued-by-us': {
    title: 'Esta nota foi emitida por você',
    body: 'É uma venda sua, não uma compra. Importá-la registraria uma compra de você mesmo.',
    action: null,
  },
  'not-addressed-to-us': {
    title: 'Esta nota foi endereçada a outro CNPJ',
    body: 'Confira se o arquivo é o certo, ou se falta cadastrar a empresa destinatária.',
    action: null,
  },
}

type Decision = 'link' | 'create' | 'skip' | 'undecided'

export const ImportarNfePage = (): ReactNode => {
  const { allows } = useAuth()
  const workspace = useWorkspace()
  const fileInput = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [analysis, setAnalysis] = useState<NfeAnalysis | null>(null)
  const [decisions, setDecisions] = useState<Record<number, Decision>>({})
  const [targets, setTargets] = useState<Record<number, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{
    purchaseId: string
    created: number
    linked: number
    skipped: number
  } | null>(null)

  const canWrite = allows('purchasing.write')

  const analyse = useMutation({
    mutationFn: async (chosen: File) => await analyzeNfe(chosen),
    onSuccess: (response) => {
      setError(null)
      setDone(null)
      setAnalysis(response.analysis)

      /*
       * A `link` the machine is certain of starts ticked; a `review` does not.
       *
       * Certain means a barcode or a supplier code this shop has resolved
       * before — evidence, not resemblance. Everything else starts undecided,
       * and an undecided line is not imported.
       */
      setDecisions(
        Object.fromEntries(
          response.analysis.items.map((item) => [
            item.number,
            item.action === 'link'
              ? 'link'
              : item.action === 'create'
                ? 'create'
                : 'undecided',
          ])
        )
      )
      setTargets(
        Object.fromEntries(
          response.analysis.items.flatMap((item) =>
            item.variantId === null ? [] : [[item.number, item.variantId]]
          )
        )
      )
    },
    onError: (cause: unknown) => {
      setAnalysis(null)
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível ler o arquivo.'
      )
    },
  })

  const send = useMutation({
    mutationFn: async () => {
      if (file === null || analysis === null) {
        throw new ApiError(422, 'no_file', 'Escolha o arquivo novamente.')
      }

      const chosen = analysis.items.flatMap<NfeItemDecision>((item) => {
        const decision = decisions[item.number] ?? 'undecided'

        // An undecided line is not imported. F018 settled that: defaulting an
        // unanswered question to "yes" makes the review theatre.
        if (decision === 'undecided') return []

        if (decision === 'link') {
          const variantId = targets[item.number] ?? item.variantId
          return variantId === null
            ? []
            : [{ number: item.number, decision: 'link', variantId }]
        }

        return [{ number: item.number, decision }]
      })

      return await importNfe(file, chosen)
    },
    onSuccess: (outcome) => {
      setError(null)
      setDone(outcome)
      setAnalysis(null)
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível importar a nota.'
      )
    },
  })

  const pending = useMemo(
    () =>
      (analysis?.items ?? []).filter(
        (item) => (decisions[item.number] ?? 'undecided') === 'undecided'
      ).length,
    [analysis, decisions]
  )

  const willImport = useMemo(
    () =>
      (analysis?.items ?? []).filter((item) => {
        const decision = decisions[item.number] ?? 'undecided'
        return decision === 'link' || decision === 'create'
      }).length,
    [analysis, decisions]
  )

  /*
   * O que "Aceitar as sugestões" aceita — F248.
   *
   * Aceitava TUDO o que esperava decisão, inclusive o "pouco parecido": um
   * clique ligava a linha da nota a um produto que só lembra o da nota, e o
   * custo dela ia parar no produto errado quando a compra fosse recebida.
   * Agora só o que tem evidência (código) ou é ao menos "parecido"; o pouco
   * parecido fica para a pessoa decidir, olhando.
   */
  const acceptable = useMemo(
    () =>
      (analysis?.items ?? []).filter(
        (item) =>
          (decisions[item.number] ?? 'undecided') === 'undecided' &&
          (item.variantId === null ||
            item.confidence === null ||
            item.confidence >= LIKELY)
      ),
    [analysis, decisions]
  )
  const doubtful = pending - acceptable.length

  const refusal = analysis?.refusal ?? null

  return (
    <Page>
      <PageHeader
        title="Importar NF-e"
        description="O XML do fornecedor vira uma compra em rascunho. É a nota que traz o código de barras, o NCM e — o que importa — o custo."
      />

      {/* Step 1 — the file */}
      <div className="flex flex-wrap items-center gap-3 rounded-(--radius-md) border border-(--border-subtle) p-4">
        <FileCode2
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-(--text-muted)"
        />
        <div className="flex flex-col">
          <span className="text-(length:--text-sm) text-(--text-primary)">
            {file === null ? 'Nenhum arquivo escolhido' : file.name}
          </span>
          <span className="text-(length:--text-xs) text-(--text-muted)">
            Um XML de NF-e (modelo 55). Nada é gravado até você confirmar.
          </span>
        </div>

        <input
          ref={fileInput}
          type="file"
          accept=".xml,text/xml,application/xml"
          className="sr-only"
          onChange={(event) => {
            const chosen = event.target.files?.[0] ?? null
            setFile(chosen)
            setDone(null)
            if (chosen !== null) analyse.mutate(chosen)
          }}
        />

        <Button
          variant="secondary"
          size="sm"
          className="ml-auto"
          disabled={!canWrite || analyse.isPending}
          leadingIcon={<Upload aria-hidden="true" className="h-4 w-4" />}
          onClick={() => {
            fileInput.current?.click()
          }}
        >
          {file === null ? 'Escolher XML' : 'Trocar arquivo'}
        </Button>
      </div>

      {error === null ? null : (
        <p
          role="alert"
          className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-sm) text-(--danger)"
        >
          {error}
        </p>
      )}

      {analyse.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : null}

      {/* The import succeeded. */}
      {done === null ? null : (
        <div className="flex flex-col gap-3 rounded-(--radius-md) border border-(--success) bg-(--success-subtle) p-4">
          <div className="flex items-center gap-2 text-(--success)">
            <CheckCircle2 aria-hidden="true" className="h-5 w-5" />
            <span className="font-(--weight-semibold)">
              Compra criada em rascunho
            </span>
          </div>
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            {`${counted(done.created, 'produto criado', 'produtos criados')}, ${counted(done.linked, 'vinculado', 'vinculados')}, ${counted(done.skipped, 'ignorado', 'ignorados')}.`}{' '}
            Nada entrou no estoque ainda: confirme a compra e registre o
            recebimento quando a mercadoria chegar.
          </p>
          <Button
            variant="primary"
            size="sm"
            className="self-start"
            leadingIcon={<ArrowRight aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              workspace.open({ module: 'compras' })
            }}
          >
            Ver em Compras
          </Button>
        </div>
      )}

      {/* Step 2 — the invoice, and whether it can be one at all */}
      {analysis === null ? null : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-(--radius-md) border border-(--border-subtle) p-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-(length:--text-xs) uppercase text-(--text-muted)">
                Emitente
              </span>
              <span className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
                {analysis.issuer.tradeName ?? analysis.issuer.legalName}
              </span>
              <span className="font-(--font-mono) text-(length:--text-xs) text-(--text-secondary)">
                {analysis.issuer.cnpj ?? analysis.issuer.cpf ?? '—'}
              </span>
            </div>

            <div className="flex flex-col gap-0.5">
              <span className="text-(length:--text-xs) uppercase text-(--text-muted)">
                Nota
              </span>
              <span className="font-(--font-mono) text-(length:--text-sm) text-(--text-primary)">
                nº {analysis.number ?? '—'} · série {analysis.series ?? '—'}
              </span>
            </div>

            <div className="flex flex-col items-end gap-0.5">
              <span className="text-(length:--text-xs) uppercase text-(--text-muted)">
                Total da nota
              </span>
              <span className="font-(--font-mono) text-(length:--text-lg) text-(--text-primary)">
                {formatMoney(parseAmount(analysis.total) ?? ZERO_AMOUNT)}
              </span>
            </div>
          </div>

          {refusal === null ? null : (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) p-4"
            >
              <div className="flex items-center gap-2 text-(--warning)">
                <AlertTriangle aria-hidden="true" className="h-5 w-5" />
                <span className="font-(--weight-semibold)">
                  {REFUSALS[refusal].title}
                </span>
              </div>
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                {REFUSALS[refusal].body}
              </p>

              {/* The invoice's own data, so registering is copying rather than
                  hunting. This is what makes stopping cost a click. */}
              {REFUSALS[refusal].action === null ? null : (
                <>
                  <dl className="grid gap-x-6 gap-y-1 text-(length:--text-xs) sm:grid-cols-2">
                    {(
                      [
                        ['Razão social', analysis.issuer.legalName],
                        ['Nome fantasia', analysis.issuer.tradeName],
                        ['CNPJ', analysis.issuer.cnpj ?? analysis.issuer.cpf],
                        [
                          'Inscrição estadual',
                          analysis.issuer.stateRegistration,
                        ],
                        [
                          'Município',
                          analysis.issuer.city === null
                            ? null
                            : `${analysis.issuer.city}${analysis.issuer.state === null ? '' : `/${analysis.issuer.state}`}`,
                        ],
                      ] as const
                    ).map(([label, value]) =>
                      value === null ? null : (
                        <div key={label} className="flex gap-2">
                          <dt className="text-(--text-muted)">{label}:</dt>
                          <dd className="text-(--text-primary)">{value}</dd>
                        </div>
                      )
                    )}
                  </dl>

                  <Button
                    variant="secondary"
                    size="sm"
                    className="self-start"
                    leadingIcon={
                      <UserPlus aria-hidden="true" className="h-4 w-4" />
                    }
                    onClick={() => {
                      workspace.open({ module: 'pessoas' })
                    }}
                  >
                    Abrir Pessoas
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Step 3 — one decision per line */}
          {refusal !== null ? null : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <p
                  aria-live="polite"
                  className="text-(length:--text-sm) text-(--text-secondary)"
                >
                  {pending === 0
                    ? `${String(willImport)} de ${items(analysis.items.length)} ${willImport === 1 ? 'será importado' : 'serão importados'}.`
                    : `${items(pending)} ainda ${pending === 1 ? 'espera' : 'esperam'} uma decisão — ${pending === 1 ? 'ele não será importado' : 'eles não serão importados'} assim.`}
                </p>

                <div className="ml-auto flex gap-2">
                  {doubtful > 0 ? (
                    <span className="self-center text-(length:--text-xs) text-(--text-muted)">
                      {doubtful === 1
                        ? '1 pouco parecida fica para você decidir'
                        : `${String(doubtful)} pouco parecidas ficam para você decidir`}
                    </span>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={acceptable.length === 0}
                    onClick={() => {
                      const accepted = new Set(
                        acceptable.map((item) => item.number)
                      )
                      setDecisions((current) =>
                        Object.fromEntries(
                          analysis.items.map((item) => [
                            item.number,
                            accepted.has(item.number)
                              ? item.variantId === null
                                ? 'create'
                                : 'link'
                              : (current[item.number] ?? 'undecided'),
                          ])
                        )
                      )
                    }}
                  >
                    {acceptable.length === 1
                      ? 'Aceitar 1 sugestão'
                      : `Aceitar ${String(acceptable.length)} sugestões`}
                  </Button>
                </div>
              </div>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Item da nota</TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Qtd.
                      </TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Custo
                      </TableHeaderCell>
                      <TableHeaderCell>Correspondência</TableHeaderCell>
                      <TableHeaderCell>O que fazer</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analysis.items.map((item) => {
                      const decision = decisions[item.number] ?? 'undecided'

                      return (
                        <TableRow key={item.number}>
                          <TableCell>
                            <span className="block text-(--text-primary)">
                              {item.description}
                            </span>
                            <span className="text-(length:--text-xs) text-(--text-muted)">
                              cód. fornecedor {item.supplierCode}
                              {item.gtin === null ? null : (
                                <>
                                  {' · '}
                                  <Barcode
                                    aria-hidden="true"
                                    className="inline h-3 w-3"
                                  />{' '}
                                  {item.gtin}
                                </>
                              )}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-(--font-mono)">
                            {item.quantity.replace(/\.?0+$/, '')} {item.unit}
                          </TableCell>
                          <TableCell className="text-right font-(--font-mono)">
                            {formatMoney(
                              parseAmount(item.lineTotal) ?? ZERO_AMOUNT
                            )}
                          </TableCell>
                          <TableCell>
                            {item.ourDescription === null ? (
                              <span className="text-(length:--text-xs) text-(--text-muted)">
                                {BASIS_LABELS[item.basis]}
                              </span>
                            ) : (
                              <div className="flex flex-col gap-0.5">
                                <span className="text-(length:--text-sm) text-(--text-primary)">
                                  {item.ourDescription}
                                </span>
                                <span className="flex items-center gap-1 text-(length:--text-xs) text-(--text-muted)">
                                  {item.basis === 'gtin' ? (
                                    <Barcode
                                      aria-hidden="true"
                                      className="h-3 w-3"
                                    />
                                  ) : item.basis === 'supplier-code' ? (
                                    <Link2
                                      aria-hidden="true"
                                      className="h-3 w-3"
                                    />
                                  ) : (
                                    <Sparkles
                                      aria-hidden="true"
                                      className="h-3 w-3"
                                    />
                                  )}
                                  {BASIS_LABELS[item.basis]}
                                  {item.confidence === null
                                    ? null
                                    : ` · ${confidenceLabel(item.confidence)}`}
                                </span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Select
                                value={decision}
                                aria-label={`O que fazer com ${item.description}`}
                                className="w-52"
                                onChange={(event) => {
                                  setDecisions((current) => ({
                                    ...current,
                                    [item.number]: event.target
                                      .value as Decision,
                                  }))
                                }}
                              >
                                <option value="undecided">Decidir…</option>
                                {item.variantId === null ? null : (
                                  <option value="link">É este produto</option>
                                )}
                                <option value="create">
                                  Criar produto novo
                                </option>
                                <option value="skip">
                                  Não é produto — ignorar
                                </option>
                              </Select>

                              {decision === 'undecided' ? (
                                <Badge tone="warning">Pendente</Badge>
                              ) : decision === 'create' ? (
                                <PackagePlus
                                  aria-label="Cria um produto"
                                  className="h-4 w-4 text-(--text-muted)"
                                />
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <div className="flex items-center justify-end gap-3">
                <span className="text-(length:--text-xs) text-(--text-muted)">
                  A compra nasce em rascunho. O estoque só se move no
                  recebimento.
                </span>
                <Button
                  variant="primary"
                  disabled={!canWrite || send.isPending || willImport === 0}
                  onClick={() => {
                    send.mutate()
                  }}
                >
                  Importar {items(willImport)}
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {analysis === null && done === null && !analyse.isPending ? (
        <EmptyState
          icon={<FileCode2 aria-hidden="true" className="h-8 w-8" />}
          title="Escolha o XML da nota"
          description={
            canWrite
              ? 'O arquivo é lido aqui e nada é gravado até você conferir item por item.'
              : 'Você não tem permissão para importar compras.'
          }
        />
      ) : null}
    </Page>
  )
}
